import { trackEvent } from '@utils/analytics'
import { useUltraPlayStore, useConfigurationStore, ULTRA_PLAY_SPIN_OPTIONS } from '@stores'

// Ultra Play's GA4 event vocabulary. Keeping every event name and parameter shape in this one file
// means call sites stay one-liners and the reporting side has a single place to read the contract from.
//
// Deliberately NOT tracked: one event per spin. A session can run 1000+ spins, which would blow past
// GA4's event volume limits. Instead a session emits a `progress` event every PROGRESS_EVERY_SPINS
// spins, so a session that never reaches a clean end is still measurable up to its last checkpoint.

const PROGRESS_EVERY_SPINS = 25

// Bet/spins can change on every tick of a slider drag. Report only where the player settled.
const SELECTION_SETTLE_MS = 800

let sessionStartedAt = 0
let lastProgressSpin = 0
let settleTimer: ReturnType<typeof setTimeout> | undefined
let isInstalled = false

// Every event carries the session's running totals, which is what you want when reading a single
// session — but a report that sums them across the checkpoints and the final event double-counts
// (26 spins arrive as a 25 checkpoint plus a 26 end = 51). These deltas are the summable numbers:
// adding spins_delta over `progress` + `session_ended` gives the true bet count, whichever of those
// events a session managed to emit.
let lastCheckpoint = { spins: 0, bet: 0, win: 0 }

const checkpointDeltas = () => {
    const { currentSpinNumber, totalBet, totalWin } = useUltraPlayStore.getState()

    const deltas = {
        spins_delta: currentSpinNumber - lastCheckpoint.spins,
        bet_delta: Number((totalBet - lastCheckpoint.bet).toFixed(2)),
        win_delta: Number((totalWin - lastCheckpoint.win).toFixed(2)),
    }

    lastCheckpoint = { spins: currentSpinNumber, bet: totalBet, win: totalWin }

    return deltas
}

const betAmountAt = (index: number): number => {
    const { betOptions } = useConfigurationStore.getState()

    if (!betOptions.length) return 0

    const option = betOptions[Math.max(0, Math.min(index, betOptions.length - 1))]

    return typeof option === 'number' ? option : option.betAmount
}

// Dimensions attached to every session event, read from the store at send time so no call site has to
// thread them through.
const sessionParams = () => {
    const {
        selectedBetIndex,
        selectedSpinsIndex,
        currentSpinNumber,
        totalBet,
        totalWin,
        selectedBuyBonusFeatureName,
        stopOnFeature,
    } = useUltraPlayStore.getState()
    const selectedSpins = ULTRA_PLAY_SPIN_OPTIONS[selectedSpinsIndex]

    return {
        bet_index: selectedBetIndex,
        // The index alone is meaningless across games and currencies — report the money too.
        bet_amount: betAmountAt(selectedBetIndex),
        // -1 is the "infinite" option; report it as such rather than as a count.
        selected_spins: selectedSpins === -1 ? 'infinite' : selectedSpins,
        spins_played: currentSpinNumber,
        total_bet: totalBet,
        total_win: totalWin,
        net_result: Number((totalWin - totalBet).toFixed(2)),
        feature: selectedBuyBonusFeatureName,
        stop_on_feature: stopOnFeature,
        session_seconds: sessionStartedAt ? Math.round((Date.now() - sessionStartedAt) / 1000) : 0,
    }
}

const send = (name: string, params: Record<string, unknown> = {}) => trackEvent(name, { ...sessionParams(), ...params })

export type UltraPlaySessionEndReason =
    | 'completed'
    | 'closed'
    | 'stop_on_feature'
    | 'no_connection'
    | 'abandoned'
    | 'insufficient_balance'

export type UltraPlayCloseReason = 'user' | 'stop_on_feature' | 'feature_dialog' | 'non_autoplayable_feature_completed'

// A session that is stopped and then closed would otherwise report twice — the recorder ends it, then
// closeUltraPlay() sees a stale status on some paths. Only the first end of a given session counts.
let hasEndedCurrentSession = false

const endSession = (reason: UltraPlaySessionEndReason) => {
    if (hasEndedCurrentSession) return

    hasEndedCurrentSession = true
    send('ultra_play_session_ended', { reason, ...checkpointDeltas() })

    // A second, deliberately minimal event whose only job is the session's bet tally. `value` and
    // `currency` are GA4's reserved names, so the money staked shows up under the built-in "Event
    // value" metric with no custom-definition setup at all — unlike every other parameter here, which
    // stays invisible in reports until it is registered. `bets` still needs registering to be summed,
    // but it is safe to sum: this event fires exactly once per session, so there is nothing to
    // double-count against the progress checkpoints.
    const { currentSpinNumber, totalBet, totalWin } = useUltraPlayStore.getState()
    const { currency } = useConfigurationStore.getState()

    trackEvent('ultra_play_bets', {
        bets: currentSpinNumber,
        value: totalBet,
        currency: currency || undefined,
        total_win: totalWin,
        net_result: Number((totalWin - totalBet).toFixed(2)),
        reason,
    })

    sessionStartedAt = 0
}

/**
 * Store subscription + unload hook. Installed on first open rather than at import time so nothing is
 * observed in games that never show Ultra Play.
 */
const install = () => {
    if (isInstalled) return

    isInstalled = true

    useUltraPlayStore.subscribe((state, prev) => {
        // One event per recorded bet, so GA4's plain Event count *is* the number of bets — no custom
        // metric to register, no parameter to sum. Kept deliberately lean: this is the one event that
        // scales with session length. The guard is `>` because removeMostRecentSpin() rolls the counter
        // back when a phantom spin is dropped, and a rollback must not re-report.
        if (state.currentSpinNumber > prev.currentSpinNumber) {
            const spin = state.spins[0]

            trackEvent('ultra_play_bet', {
                spin_number: state.currentSpinNumber,
                // GA4 reserved names: the staked amount lands in the built-in "Event value" metric.
                value: spin?.bet ?? 0,
                currency: useConfigurationStore.getState().currency || undefined,
                win: spin?.win ?? 0,
                feature: state.selectedBuyBonusFeatureName,
            })
        }

        if (state.currentSpinNumber >= lastProgressSpin + PROGRESS_EVERY_SPINS) {
            lastProgressSpin = state.currentSpinNumber
            send('ultra_play_progress', checkpointDeltas())
        }

        if (state.selectedBetIndex !== prev.selectedBetIndex || state.selectedSpinsIndex !== prev.selectedSpinsIndex) {
            clearTimeout(settleTimer)
            settleTimer = setTimeout(() => {
                send('ultra_play_selection_changed', { phase: state.status === 'READY' ? 'setup' : 'active' })
            }, SELECTION_SETTLE_MS)
        }
    })

    // A closed tab or a navigated-away iframe never reaches a session end, so a long session would
    // vanish entirely. GA4 transports events with sendBeacon, which survives the unload.
    window.addEventListener('pagehide', () => {
        const { status } = useUltraPlayStore.getState()

        if (status === 'PLAYING' || status === 'PAUSED') {
            endSession('abandoned')
        }
    })
}

export const ultraPlayAnalytics = {
    opened: () => {
        install()
        send('ultra_play_opened')
    },
    closed: (reason: UltraPlayCloseReason = 'user') => send('ultra_play_closed', { reason }),

    sessionStarted: (isContinued: boolean) => {
        sessionStartedAt = Date.now()
        lastProgressSpin = 0
        lastCheckpoint = { spins: 0, bet: 0, win: 0 }
        hasEndedCurrentSession = false
        send('ultra_play_session_started', { is_continued: isContinued })
    },
    sessionPaused: (source: 'user' | 'feature' = 'user') => send('ultra_play_session_paused', { source }),
    sessionResumed: (source: 'user' | 'feature' = 'user') => send('ultra_play_session_resumed', { source }),
    sessionEnded: endSession,

    featureSelected: (featureName: string, previousFeatureName: string) =>
        send('ultra_play_feature_selected', { feature: featureName, previous_feature: previousFeatureName }),
    stopOnFeatureToggled: (enabled: boolean) => send('ultra_play_stop_on_feature_toggled', { enabled }),
    settingsOpened: () => send('ultra_play_settings_opened'),

    // Fired when the engine reports a feature start during a session — this is the "how many bonuses
    // did Ultra Play actually hit" counter, independent of how the session later ends.
    featureTriggered: (featureName: string, isAutoplayable: boolean) =>
        send('ultra_play_feature_triggered', { triggered_feature: featureName, is_autoplayable: isAutoplayable }),
    featureDialogShown: (featureName: string, isAutoplayable: boolean) =>
        send('ultra_play_feature_dialog_shown', { triggered_feature: featureName, is_autoplayable: isAutoplayable }),
    featureDialogChoice: (choice: 'continue_here' | 'play_in_game') =>
        send('ultra_play_feature_dialog_choice', { choice }),

    insufficientBalance: (at: 'play' | 'resume') => send('ultra_play_insufficient_balance', { at }),
    insufficientBalanceDismissed: () => send('ultra_play_insufficient_balance_dismissed'),
    noConnection: () => send('ultra_play_no_connection'),
}
